import React from 'react';
import OpenPreviewProductDetail from '../../../../../containers/app/common/OpenPreviewProductDetail';
import TableFilterInput from '../../../../../components/table/TableFilterInput';
import Button from '../../../../../components/Button';
import Select from '../../../../../components/input/Select';
import OpenBrandDetail from '../../../../../components/link/OpenBrandDetail';
import OpenProductDetail from '../../../../../components/link/OpenProductDetail';
import OpenOrderItemDetail from '../../../../../components/link/OpenOrderItemDetail';
import OpenUserDetail from '../../../../../components/link/OpenUserDetail';
import DateRangePicker from '../../../../../components/datePicker/DateRangePicker';
import {
  addAllItemToSelectList,
  reDirectPage,
  setClearDash,
  setColumnWidth,
  setDash,
  setDetailPageUrl,
  setPhoneFormat,
  setNumberWithComma,
} from '../../../../../lib/common';
import { setDateFormat } from '../../../../../lib/moment';
import SendMessageService from '../../../../../containers/app/common/SendMessageService';
import { setOrderMessage } from '../../../../../lib/utils/orderutils';
import OpenOrderReturnItemDetail from '../../../../../components/link/OpenOrderReturnItemDetail';
import OpenReturnExchangeAttachment from '../../../../../containers/app/common/OpenReturnExchangeAttachment';
import { AMONDZ_MEMBER_TYPE, DATE_FORMAT } from '../../../../../constants/service/common/utilConstants';
import {
  ORDER_ACCEPT_MANAGER_TEXT,
  ORDER_GIFT_STATUS_SELECT_LIST,
  ORDER_GIFT_STATUS_TEXT,
  ORDER_TYPE_SELECT_LIST,
  ORDER_TYPE_TEXT,
  ORDER_RETURN_STATUS,
  ORDER_RETURN_STATUS_TEXT,
  ORDER_RETURN_TYPE_SELECT_LIST,
  ORDER_RETURN_TYPE_TEXT,
  PAYMENT_METHOD_TYPE_SELECT_LIST,
  PAY_METHOD_TYPE_TEXT,
  RETURN_EXCHANGE_REASON_TYPE,
} from '../../../../../constants/service/app/orderConstants';
import { ORDER_RETURN_DETAIL_URL_PATH } from '../../../../../constants/service/common/urlConstants';

/**
 *  주문 반품 리스트 컬럼
 */
const columns = (data) => {
  const { list, filterOption, onFilter, mainProductCategorySelectList } = data;
  return [
    {
      accessor: 'orderType',
      Header: '주문 타입',
      filterable: true,
      width: setColumnWidth(list, 'orderType'),
      Cell: (row) => setDash(ORDER_TYPE_TEXT[row.value]),
      Filter: () => (
        <Select
          isTable
          list={addAllItemToSelectList(ORDER_TYPE_SELECT_LIST)}
          value={filterOption.orderType}
          onChange={(orderType) => onFilter({ orderType })}
        />
      ),
    },
    {
      accessor: 'giftStatus',
      Header: '선물 상태',
      filterable: true,
      width: 150,
      Cell: (row) => setDash(ORDER_GIFT_STATUS_TEXT[row.value]),
      Filter: () => (
        <Select
          isTable
          list={addAllItemToSelectList(ORDER_GIFT_STATUS_SELECT_LIST)}
          value={filterOption.giftStatus}
          onChange={(giftStatus) => onFilter({ giftStatus })}
        />
      ),
    },
    {
      Header: '반품 정보',
      fixed: 'left',
      columns: [
        {
          accessor: 'orderId',
          Header: '주문번호',
          filterable: true,
          width: 150,
          Filter: () => (
            <TableFilterInput option={filterOption.orderId} onFilter={(orderId) => onFilter({ orderId })} />
          ),
        },
        {
          accessor: 'orderItemId',
          Header: '상품 주문번호',
          filterable: true,
          width: 150,
          Cell: (row) => <OpenOrderItemDetail text={row.value} orderItemId={row.value} />,
          Filter: () => (
            <TableFilterInput option={filterOption.orderItemId} onFilter={(orderItemId) => onFilter({ orderItemId })} />
          ),
        },
        {
          accessor: 'orderReturnId',
          Header: '반품 번호',
          Cell: (row) =>
            /**
             *  반품 승인, 반품 완료 일 경우에만
             *  새로운 탭에서 반품 상세 페이지를 여는 anchor 노출
             */
            row.original.orderReturnStatus === ORDER_RETURN_STATUS.RETURN_DONE ||
            row.original.orderReturnStatus === ORDER_RETURN_STATUS.REFUND_DONE ? (
              <OpenOrderReturnItemDetail text={row.value} orderItemId={row.original.orderItemId} />
            ) : (
              row.value
            ),
        },
        {
          accessor: 'orderReturnStatus',
          Header: '반품 처리 상태',
          Cell: (row) =>
            /**
             *  반품 완료일 경우에만 환불 완료처리를 할 수 있게 버튼 노출
             */
            row.value === ORDER_RETURN_STATUS.RETURN_DONE ? (
              <Button
                isRadius
                color="primary"
                text={ORDER_RETURN_STATUS_TEXT[row.value]}
                onClick={() =>
                  reDirectPage({ path: setDetailPageUrl(ORDER_RETURN_DETAIL_URL_PATH, row.original.orderItemId) })
                }
              />
            ) : (
              ORDER_RETURN_STATUS_TEXT[row.value]
            ),
        },
        {
          accessor: 'returnType',
          Header: '반품 사유',
          filterable: true,
          width: 120,
          Filter: () => (
            <Select
              list={ORDER_RETURN_TYPE_SELECT_LIST}
              onChange={(returnType) => onFilter({ returnType })}
              defaultValue={filterOption.returnType}
              value={filterOption.returnType}
              isTable={true}
              size="large"
            />
          ),
          Cell: (row) => (
            <OpenReturnExchangeAttachment
              title="반품사유"
              orderReturnExchangeId={row.original.orderReturnId}
              type={RETURN_EXCHANGE_REASON_TYPE.RETURN_REQUEST}
              text={ORDER_RETURN_TYPE_TEXT[row.value]}
              reason={row.original.returnReason}
            />
          ),
        },
        {
          accessor: 'acceptManager',
          Header: '반품 접수자',
          width: 100,
          Cell: (row) => setDash(ORDER_ACCEPT_MANAGER_TEXT[row.value]),
        },
      ],
    },
    {
      Header: '반품 거부',
      columns: [
        {
          accessor: 'rejectReason',
          Header: '거부 사유',
          width: 110,
          Cell: (row) => (
            <OpenReturnExchangeAttachment
              title="반품 거부사유"
              orderReturnExchangeId={row.original.orderReturnId}
              type={RETURN_EXCHANGE_REASON_TYPE.RETURN_REJECT}
              text="사유 보기"
              reason={row.value}
            />
          ),
        },
        {
          accessor: 'rejectDate',
          Header: '거부 일시',
          width: 130,
          Cell: (row) => setDateFormat(row.value, DATE_FORMAT.YMD_HM_DASH_FORMAT),
        },
      ],
    },
    {
      Header: '반품 정보',
      columns: [
        {
          accessor: 'refundExpectAmount',
          Header: '구매자의 환불 예정 금액',
          width: 160,
          Cell: (row) => setNumberWithComma(row.value),
        },
        {
          accessor: 'refundSellerAmount',
          Header: '판매자의 환불 예정 금액',
          width: 160,
          Cell: (row) => setNumberWithComma(row.value),
        },
        {
          accessor: 'refundActualAmount',
          Header: '실제 고객 환불금액',
          width: 160,
          Cell: (row) => setNumberWithComma(row.value),
        },
        {
          accessor: 'paymentDate',
          Header: '결제 일시',
          sortable: true,
          filterable: true,
          width: 320,
          Cell: (row) => setDateFormat(row.value, DATE_FORMAT.YMD_HM_DASH_FORMAT),
          Filter: () => (
            <DateRangePicker
              value={filterOption.paymentDate}
              outFormat={DATE_FORMAT.YMD_DASH_FORMAT}
              onSelect={(data) => onFilter({ paymentDate: data })}
            />
          ),
        },
        {
          accessor: 'returnRequestDate',
          Header: '반품 요청 일시',
          sortable: true,
          filterable: true,
          width: 320,
          Cell: (row) => setDateFormat(row.value, DATE_FORMAT.YMD_HM_DASH_FORMAT),
          Filter: () => (
            <DateRangePicker
              value={filterOption.returnRequestDate}
              outFormat={DATE_FORMAT.YMD_DASH_FORMAT}
              onSelect={(data) => onFilter({ returnRequestDate: data })}
            />
          ),
        },
        {
          accessor: 'returnReceiveDate',
          Header: '수거 완료 일시',
          sortable: true,
          filterable: true,
          width: 320,
          Cell: (row) => setDateFormat(row.value, DATE_FORMAT.YMD_HM_DASH_FORMAT),
          Filter: () => (
            <DateRangePicker
              value={filterOption.returnReceiveDate}
              outFormat={DATE_FORMAT.YMD_DASH_FORMAT}
              onSelect={(data) => onFilter({ returnReceiveDate: data })}
            />
          ),
        },
        {
          accessor: 'returnDelayDate',
          Header: '반품 처리 지연 일시',
          width: 320,
          Cell: (row) => setDateFormat(row.value, DATE_FORMAT.YMD_HM_DASH_FORMAT),
        },
        {
          accessor: 'returnCompleteDate',
          Header: '반품 승인 일시',
          sortable: true,
          filterable: true,
          width: 320,
          Cell: (row) => setDateFormat(row.value, DATE_FORMAT.YMD_HM_DASH_FORMAT),
          Filter: () => (
            <DateRangePicker
              value={filterOption.returnCompleteDate}
              outFormat={DATE_FORMAT.YMD_DASH_FORMAT}
              onSelect={(data) => onFilter({ returnCompleteDate: data })}
            />
          ),
        },
        {
          accessor: 'refundCompleteDate',
          Header: '반품 완료 일시',
          sortable: true,
          filterable: true,
          width: 320,
          Cell: (row) => setDateFormat(row.value, DATE_FORMAT.YMD_HM_DASH_FORMAT),
          Filter: () => (
            <DateRangePicker
              value={filterOption.refundCompleteDate}
              outFormat={DATE_FORMAT.YMD_DASH_FORMAT}
              onSelect={(data) => onFilter({ refundCompleteDate: data })}
            />
          ),
        },
      ],
    },
    {
      Header: '구매 상품 정보',
      columns: [
        {
          accessor: 'brandName',
          Header: '브랜드명',
          resizable: true,
          filterable: true,
          width: setColumnWidth(list, 'brandName'),
          Cell: (row) => <OpenBrandDetail text={row.value} storeId={row.original.storeId} />,
          Filter: () => (
            <TableFilterInput option={filterOption.brandName} onFilter={(brandName) => onFilter({ brandName })} />
          ),
        },
        {
          accessor: 'storeId',
          Header: '브랜드 ID',
          filterable: true,
          width: 70,
          Cell: (row) => <OpenBrandDetail text={row.value} storeId={row.value} />,
          Filter: () => (
            <TableFilterInput option={filterOption.storeId} onFilter={(storeId) => onFilter({ storeId })} />
          ),
        },
        {
          accessor: 'productId',
          Header: '상품번호',
          filterable: true,
          Cell: (row) => <OpenProductDetail text={row.value} productId={row.value} />,
          Filter: () => (
            <TableFilterInput option={filterOption.productId} onFilter={(productId) => onFilter({ productId })} />
          ),
        },
        {
          accessor: 'productName',
          Header: '상품명',
          className: 'align-left',
          resizable: true,
          filterable: true,
          width: setColumnWidth(list, 'productName'),
          Cell: (row) => <OpenPreviewProductDetail text={row.value} productId={row.original.productId} />,
          Filter: () => (
            <TableFilterInput option={filterOption.productName} onFilter={(productName) => onFilter({ productName })} />
          ),
        },
        {
          accessor: 'mainCategory',
          Header: '1차 카테고리',
          width: setColumnWidth(list, 'mainCategory') + 40,
          filterable: true,
          Filter: () => (
            <Select
              list={addAllItemToSelectList(mainProductCategorySelectList)}
              onChange={(mainCategory) =>
                onFilter({
                  mainCategory,
                  subCategory: null,
                })
              }
              defaultValue={filterOption.mainCategory}
              value={filterOption.mainCategory}
              isTable={true}
              size="large"
            />
          ),
        },
        {
          accessor: 'productOptionName',
          Header: '옵션정보',
          resizable: true,
          width: setColumnWidth(list, 'productOptionName'),
          Cell: (row) => setDash(row.value),
        },
        {
          accessor: 'quantity',
          Header: '수량',
          Cell: (row) => setNumberWithComma(row.value),
        },
        {
          accessor: 'productOriginalPrice',
          Header: '상품 가격(정가)',
          width: 130,
          Cell: (row) => setNumberWithComma(row.value),
        },
        {
          accessor: 'productDiscountPrice',
          Header: '상품 할인액(차감된 금액)',
          width: 160,
          Cell: (row) => setNumberWithComma(row.value),
        },
        {
          accessor: 'productOptionPrice',
          Header: '옵션 가격',
          Cell: (row) => setNumberWithComma(row.value),
        },
        {
          accessor: 'usedCoupon',
          Header: '쿠폰',
          Cell: (row) => setNumberWithComma(row.value),
        },
        {
          accessor: 'usedPoint',
          Header: '포인트',
          Cell: (row) => setNumberWithComma(row.value),
        },
        {
          accessor: 'totalPrice',
          Header: '상품별 총 주문금액',
          width: 150,
          Cell: (row) => setNumberWithComma(row.value),
        },
        {
          accessor: 'deliveryExtraFee',
          Header: '추가 배송비',
          Cell: (row) => setNumberWithComma(row.value),
        },
        {
          accessor: 'orderMessage',
          Header: '주문 메시지',
          Cell: (row) => setOrderMessage(row.value),
        },
      ],
    },
    {
      Header: '구매자 정보',
      columns: [
        {
          accessor: 'buyerAccount',
          Header: '구매자 ID',
          filterable: true,
          width: setColumnWidth(list, 'buyerAccount'),
          Cell: (row) => <OpenUserDetail text={row.value} userId={row.original.userId} />,
          Filter: () => (
            <TableFilterInput
              option={filterOption.buyerAccount}
              onFilter={(buyerAccount) => onFilter({ buyerAccount })}
            />
          ),
        },
        {
          accessor: 'buyerName',
          Header: ' 구매자명',
          filterable: true,
          width: setColumnWidth(list, 'buyerName'),
          Filter: () => (
            <TableFilterInput option={filterOption.buyerName} onFilter={(buyerName) => onFilter({ buyerName })} />
          ),
        },
        {
          accessor: 'buyerPhone',
          Header: '구매자 연락처',
          filterable: true,
          width: 170,
          Cell: (row) => (
            <SendMessageService
              text={setPhoneFormat(row.value)}
              memberType={AMONDZ_MEMBER_TYPE.USER}
              targetId={row.original.userId}
            />
          ),
          Filter: () => (
            <TableFilterInput
              option={filterOption.buyerPhone}
              onFilter={(buyerPhone) =>
                onFilter({
                  buyerPhone: setClearDash(buyerPhone),
                })
              }
            />
          ),
        },
        {
          accessor: 'recipientName',
          Header: ' 수취인명',
          filterable: true,
          width: setColumnWidth(list, 'recipientName'),
          Filter: () => (
            <TableFilterInput
              option={filterOption.recipientName}
              onFilter={(recipientName) => onFilter({ recipientName })}
            />
          ),
        },
        {
          accessor: 'recipientPhone',
          Header: '수취인 연락처',
          filterable: true,
          width: 170,
          Cell: (row) => (
            <SendMessageService
              text={setPhoneFormat(row.value)}
              memberType={AMONDZ_MEMBER_TYPE.RECIPIENT}
              targetId={row.original.orderItemId}
            />
          ),
          Filter: () => (
            <TableFilterInput
              option={filterOption.recipientPhone}
              onFilter={(recipientPhone) =>
                onFilter({
                  recipientPhone: setClearDash(recipientPhone),
                })
              }
            />
          ),
        },
        {
          accessor: 'address',
          Header: '배송지',
          resizable: true,
          width: setColumnWidth(list, 'address'),
        },
        {
          accessor: 'postCode',
          Header: '우편번호',
        },
        {
          accessor: 'message',
          Header: '배송 메시지',
          className: 'align-left',
          resizable: true,
          width: 200,
        },
      ],
    },
    {
      Header: '결제',
      columns: [
        {
          accessor: 'payMethod',
          Header: '결제수단',
          filterable: true,
          width: 170,
          Cell: (row) => PAY_METHOD_TYPE_TEXT[row.value] || '_',
          Filter: () => (
            <Select
              list={PAYMENT_METHOD_TYPE_SELECT_LIST}
              onChange={(payMethod) => onFilter({ payMethod })}
              defaultValue={filterOption.payMethod}
              value={filterOption.payMethod}
              isTable={true}
              size="large"
            />
          ),
        },
      ],
    },
  ];
};

export default columns;
